import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db6a3ki2sgkaik1und4g";

const values = [
  {
    icon: "ri-stack-line",
    title: "海量资源",
    desc: "覆盖电影、电视剧、综艺、动漫、纪录片等多个类别，持续更新精彩内容。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "清晰分类",
    desc: "多维标签筛选与排序，帮助你快速缩小范围，精准定位想看的内容。",
  },
  {
    icon: "ri-search-eye-line",
    title: "便捷查找",
    desc: "关键词模糊搜索，配合分类与标签，让每一次探索都高效顺畅。",
  },
  {
    icon: "ri-heart-3-line",
    title: "轻松观看",
    desc: "简洁大方的门户式布局，桌面与移动端一致体验，专注内容本身。",
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    document.title = "关于我们 - 色色视频";
    const tag = document.querySelector('meta[name="description"]');
    if (tag) tag.setAttribute("content", "了解色色视频的网站定位、内容范围、服务宗旨与联系方式。");
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = ((formData.get("website_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value as string);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const msgText = String(serverMsg ?? "");
      const isSpam = msgText.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setFormError(msgText && msgText.length < 160 ? msgText : "提交失败，请稍后重试或直接发送邮件联系我们。");
        setStatus("error");
        return;
      }

      setStatus("success");
      formEl.reset();
    } catch {
      setFormError("网络异常，请稍后重试或直接发送邮件联系我们。");
      setStatus("error");
    }
  };

  return (
    <div className="w-full">
      <section className="bg-primary-950">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
            <i className="ri-play-fill" aria-hidden="true"></i>
            关于色色视频
          </span>
          <h1 className="mt-4 font-heading text-4xl leading-tight text-background-50 md:text-5xl">
            让每一次观影都被认真对待
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">
            色色视频是面向中文用户的在线视频内容平台，汇聚海量且分类丰富的视频资源，帮助用户高效浏览、便捷查找与轻松观看。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-3 font-heading text-2xl text-foreground-950 md:text-3xl">
              <span className="h-7 w-1.5 rounded-full bg-primary-500" aria-hidden="true"></span>
              网站定位
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              色色视频定位为现代门户风格的在线视频内容平台，聚焦海量且分类丰富的视频资源展示与浏览。我们面向追求免费观看高清视频、影视剧、原创及多类别视频的普通互联网观众，
              希望通过清晰的内容层级与友好的浏览路径，让寻找视频这件事变得简单而愉快。
            </p>

            <h2 className="mt-10 flex items-center gap-3 font-heading text-2xl text-foreground-950 md:text-3xl">
              <span className="h-7 w-1.5 rounded-full bg-accent-500" aria-hidden="true"></span>
              内容范围
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              平台内容覆盖电影、电视剧、综艺、动画、动漫、纪录片、体育、音乐、游戏与科技等多个类别。
              每一条内容都配有封面、时长、发布时间、播放量与评分等信息，方便你在进入详情前快速判断是否符合口味。
              目前站内内容均为演示数据，用于展示平台的浏览、筛选与收藏等完整交互流程。
            </p>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {values.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                    <i className={`${item.icon} text-2xl`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-3 text-base font-medium text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-xl border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-xl text-foreground-950">联系我们</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                欢迎通过邮件或下方表单与我们取得联系，我们会认真对待每一条反馈。
              </p>
              <a
                href="mailto:contact@seseshipin.example.com"
                className="mt-4 flex items-center gap-3 rounded-lg border border-background-300 bg-background-50 px-4 py-3 transition-colors hover:border-primary-300"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-mail-line text-lg" aria-hidden="true"></i>
                </span>
                <span className="min-w-0">
                  <span className="block text-xs text-foreground-400">官方邮箱</span>
                  <span className="block truncate text-sm text-foreground-800">contact@seseshipin.example.com</span>
                </span>
              </a>
              <div className="mt-4 flex items-center gap-3 rounded-lg border border-background-300 bg-background-50 px-4 py-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-50 text-accent-600">
                  <i className="ri-time-line text-lg" aria-hidden="true"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-400">服务时间</span>
                  <span className="block text-sm text-foreground-800">工作日 09:00 - 18:00</span>
                </span>
              </div>
            </div>

            <form
              data-readdy-form
              id="db6a3ki2sgkaik1und4g"
              onSubmit={handleSubmit}
              className="mt-6 rounded-xl border border-background-200 bg-background-50 p-6"
            >
              <h2 className="font-heading text-xl text-foreground-950">在线反馈</h2>
              <p className="mt-2 text-sm text-foreground-500">填写以下信息，我们会尽快回复你。</p>

              <div className="mt-4 flex flex-col gap-3">
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground-600">您的称呼</span>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="请输入您的称呼"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground-600">邮箱地址</span>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground-600">反馈类型</span>
                  <select
                    name="topic"
                    defaultValue="内容建议"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="使用问题">使用问题</option>
                    <option value="版权相关">版权相关</option>
                    <option value="商务合作">商务合作</option>
                  </select>
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground-600">留言内容</span>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请简要描述你的问题或建议（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  ></textarea>
                </label>

                <input
                  className="website-alt-field"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-1 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-send-plane-line text-lg" aria-hidden="true"></i>
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>

                {status === "success" && (
                  <p className="flex items-center gap-2 rounded-md bg-accent-50 px-3 py-2 text-sm text-accent-700">
                    <i className="ri-checkbox-circle-line text-base" aria-hidden="true"></i>
                    提交成功，感谢你的反馈！
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-2 rounded-md bg-primary-50 px-3 py-2 text-sm text-primary-700">
                    <i className="ri-error-warning-line text-base" aria-hidden="true"></i>
                    {formError}
                  </p>
                )}
              </div>
            </form>
          </aside>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
          <div className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-2xl text-foreground-950">
              <i className="ri-shield-check-line text-primary-500" aria-hidden="true"></i>
              版权与免责声明
            </h2>
            <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              <p>
                本站为演示性质的内容平台，站内所有视频封面、标题、简介及相关数据均为演示示例，不提供真实的视频播放或下载服务。
              </p>
              <p>
                如本站演示内容无意中涉及第三方权益，请通过上方联系方式与我们联系，我们将第一时间处理。
              </p>
              <p>
                用户在本站收藏的视频信息仅保存在本地浏览器中，不会上传至服务器，清除浏览器数据后将无法恢复。
              </p>
            </div>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-4-line text-lg" aria-hidden="true"></i>
              返回首页探索视频
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}